
import React, { useState, useEffect } from "react";
import { Box, Button, Input, Textarea, FormControl, FormLabel, Flex, } from "@chakra-ui/react";
import { CustomButton, CustomToast } from "../../../../components";
import { baseUrl, useFetch } from "../../../../hooks/useFetch";
import { IDepartment } from "../../../Admin/Master/Department/model";
import { ICompanyDropdown } from "../../../Admin/Master/Branch/model";
import { useAuth } from "../../../../hooks/useAuth";
import { MultiSelectMenu } from "../../../../components/MultiSelectMenu/MultiSelectMenu";

interface announcementFormProps {
  onClose: () => void;
  reload?: boolean;
  setReload?: (reload: boolean) => void;
}
interface IBranch {
  branchId: number;
  branchName: string;
}

const AnnouncementForm = ({ onClose, reload, setReload }: announcementFormProps) => {
  const [banner, setBanner] = useState<File | null>(null);
  const { addToast } = CustomToast();
  const { fetchApi, loading } = useFetch(addToast);
  const { loginObj } = useAuth();
  const [formData, setFormData] = useState({ topic: "", description: "", duration: "", });
  const [selectedDepartments, setSelectedDepartments] = useState<number[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<number[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<number[]>([]);
  const [departments, setDepartments] = useState<IDepartment[]>([]);
  const [companies, setCompanies] = useState<ICompanyDropdown[]>([]);
  const [branchOptions, setBranchOptions] = useState<IBranch[]>([]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setBanner(file);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (name === "duration") {
      const num = Number(value);

      if (!Number.isInteger(num)) {
        addToast({ message: "Decimal values are not allowed", status: "error" });
        return;
      }

      if (num > 30) {
        addToast({ message: "Duration cannot be more than 30 days", status: "error" });
        setFormData((prev) => ({
          ...prev,
          [name]: "30",
        }));
        return;
      }

      if (num < 1 && value !== "") {
        addToast({ message: "Duration must be at least 1 day", status: "error" });
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const loadDepartments = async () => {
    const response = await fetchApi("Dropdown/department", "GET", null, null, "");
    if (response) {
      const updated = [{ id: 0, departmentName: "All Department" }, ...response];
      setDepartments(updated);
    }
  };

  const loadCompanies = async () => {
    const response = await fetchApi("Dropdown/company", "GET", null, null);
    if (response) {
      const updated = [{ company_ID: 0, company_Name: "All Company" }, ...response];
      setCompanies(updated);
    }
  };

  const loadBranches = async (companyIds: number[]) => {
    if (!companyIds.length) {
      setBranchOptions([]);
      return;
    }
    const query = companyIds.map((id) => `companyIds=${id}`).join("&");
    const response = await fetchApi(`Dropdown/MultiBranchDropDown?${query}`, "GET", null, null);
    if (response) {
      const updated = [{ branchId: 0, branchName: "All Branch" }, ...response];
      setBranchOptions(updated);
    }
  };

  const handleSubmit = async () => {
    const errors: string[] = [];

    if (!formData.topic?.trim()) errors.push("Topic is required.");
    if (!formData.description?.trim()) errors.push("Description is required.");
    if (!formData.duration?.trim()) errors.push("Duration is required.");
    if (selectedCompanies.length === 0) errors.push("Please select at least one company.");
    if (selectedBranches.length === 0) errors.push("Please select at least one branch.");
    if (selectedDepartments.length === 0) errors.push("Please select at least one department.");

    if (errors.length > 0) {
      errors.forEach((msg, index) => {
        setTimeout(() => {
          addToast({ message: msg, status: "error" });
        }, index * 200);
      });
      return;
    }
    try {
      const data = new FormData();
      data.append("topic", formData.topic);
      data.append("description", formData.description);
      data.append("duration", formData.duration);
      if (banner) data.append("banner", banner);
      const companiesToSend = selectedCompanies.filter((id) => id !== 0);
      const branchesToSend = selectedBranches.filter((id) => id !== 0);
      const departmentsToSend = selectedDepartments.filter((id) => id !== 0);
      data.append("companies", JSON.stringify(companiesToSend));
      data.append("branches", JSON.stringify(branchesToSend));
      data.append("departments", JSON.stringify(departmentsToSend));

      const response = await fetch(`${baseUrl}dashboard/annoucement`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${loginObj?.token || ""}`,
        },
        body: data,
      });

      if (response.ok) {
        addToast({ message: "Announcement posted successfully.", status: "success", });
        setReload && setReload(!reload)
        onClose();
      } else {
        const errorText = await response.text();
        addToast({ message: `Failed to post announcement: ${errorText}`, status: "error", });
      }
    } catch (error: any) {
      addToast({ message: `Error: ${error.message}`, status: "error" });
    }
  };

  useEffect(() => {
    loadCompanies();
    loadDepartments();
  }, []);

  useEffect(() => {
    loadBranches(selectedCompanies);
    setSelectedBranches([]);
  }, [selectedCompanies]);

  console.log(selectedCompanies);

  return (
    <Box>
      <FormControl mb={4} isRequired>
        <FormLabel>Announcement Topic</FormLabel>
        <Input placeholder="Topic" type="text" name="topic" value={formData.topic} onChange={handleInputChange} />
      </FormControl>

      <FormControl mb={4} isRequired>
        <FormLabel>Announcement Description</FormLabel>
        <Textarea placeholder="Description" name="description" value={formData.description} onChange={handleInputChange} />
      </FormControl>

      <FormControl mb={4} isRequired>
        <FormLabel>Announcement Duration</FormLabel>
        <Input placeholder="Duration in days" type="number" name="duration" value={formData.duration} onChange={handleInputChange} min={1} max={30} />
      </FormControl>

      <MultiSelectMenu ShowNumberOfTiles={1} overflow={true} isRequired label="Companies" placeholder="Select Company" options={companies} selectedValues={selectedCompanies} setSelectedValues={setSelectedCompanies} labelKey="company_Name" valueKey="company_ID" colorScheme="blue" />
      <MultiSelectMenu ShowNumberOfTiles={1} overflow={true} isRequired label="Branches" placeholder="Select Branch" options={branchOptions} selectedValues={selectedBranches} setSelectedValues={setSelectedBranches} labelKey="branchName" valueKey="branchId" colorScheme="purple" />
      <MultiSelectMenu ShowNumberOfTiles={1} overflow={true} isRequired label="Departments" placeholder="Select Department" options={departments} selectedValues={selectedDepartments} setSelectedValues={setSelectedDepartments} labelKey="departmentName" valueKey="id" colorScheme="green" />
      <FormControl mb={4}>
        <FormLabel>Upload Announcements Banner</FormLabel>
        <Input type="file" accept="image/*" onChange={handleFileChange} />
      </FormControl>
      <Box position="absolute" bottom="10" right="10" >
        <CustomButton
          title="Post Announcement"
          onClick={handleSubmit}
          isLoading={loading}
          className="btn_theme"
        />
      </Box>
    </Box>
  );
};

export default AnnouncementForm;
